import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Layout from '../components/layout/Layout';
import { PageHeader, Spinner } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useWindowWidth } from '../hooks/useWindowWidth';

const impoziteAPI = {
  getMele:   (an) => api.get(`/impozite?an=${an}`).then(r => r.data),
  getSumar:  (an) => api.get(`/impozite/sumar?an=${an}`).then(r => r.data),
  initiaza:  (d)  => api.post('/plati/initiaza', d),
};

const TIP_CFG = {
  cladire:   { label: 'Clădiri',              icon: '🏠', color: '#2d2d7e' },
  teren:     { label: 'Terenuri',             icon: '🌱', color: '#1d9e75' },
  transport: { label: 'Mijloace de transport',icon: '🚗', color: '#854f0b' },
};

const METODA_LABEL = {
  numerar: 'Numerar', card: 'Card', transfer: 'Transfer bancar', online: 'Online',
};

function fmt(n) {
  if (n == null) return '—';
  return parseFloat(n).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' lei';
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('ro-RO');
}
function isExpired(d) {
  return d && new Date(d) < new Date();
}
function isBefore31March() {
  const now = new Date();
  const limit = new Date(now.getFullYear(), 2, 31); // 31 martie
  return now <= limit;
}

// ── Modal plată ───────────────────────────────────────────────
function ModalPlataOnline({ prop, rata, onClose }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const isTotalaBefore31 = rata === 'integral' && isBefore31March();
  let suma, label, descriere;

  if (rata === '1') {
    suma = parseFloat(prop.rata1);
    label = 'Rata I';
    descriere = `Impozit ${prop.an} - Rata I - ${prop.adresa || prop.tip}`;
  } else if (rata === '2') {
    suma = parseFloat(prop.rata2);
    label = 'Rata II';
    descriere = `Impozit ${prop.an} - Rata II - ${prop.adresa || prop.tip}`;
  } else {
    const total = parseFloat(prop.suma_totala);
    suma = isTotalaBefore31 ? Math.round(total * 0.9 * 100) / 100 : total;
    label = 'Plată integrală' + (isTotalaBefore31 ? ' (−10% bonificație)' : '');
    descriere = `Impozit ${prop.an} - Integral - ${prop.adresa || prop.tip}`;
  }

  async function initiazaPlata() {
    setLoading(true);
    try {
      const res = await impoziteAPI.initiaza({
        suma,
        descriere,
        impozitId:  prop.impozit_id,
        impozitRata: rata,
        platitorNume: user?.tip_persoana === 'juridica'
          ? user?.denumire_firma
          : `${user?.prenume || ''} ${user?.nume || ''}`.trim(),
        platitorEmail: user?.email,
        platitorCnp:   user?.cnp,
      });
      // Redirect la EuPlătesc
      window.location.href = res.data.redirectUrl;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Eroare la inițierea plății');
      setLoading(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '400px' }}>
        <div style={{ fontSize: '36px', textAlign: 'center', marginBottom: '12px' }}>💳</div>
        <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: '700', textAlign: 'center', color: '#1a1a2e' }}>
          Plată online
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
          {prop.adresa || prop.tip}
        </p>

        <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '16px', marginBottom: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {label}
          </div>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#2d2d7e' }}>
            {fmt(suma)}
          </div>
          {isTotalaBefore31 && rata === 'integral' && (
            <div style={{ fontSize: '12px', color: '#166534', marginTop: '6px' }}>
              Înainte de 31 martie — bonificație 10% aplicată
            </div>
          )}
        </div>

        <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '12px 14px', marginBottom: '20px',
                      fontSize: '13px', color: '#1d4ed8' }}>
          Veți fi redirecționat pe platforma EuPlătesc pentru plata securizată cu cardul. La finalizare
          veți primi chitanță electronică pe email.
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '12px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '10px', cursor: 'pointer', background: '#fff' }}>
            Anulează
          </button>
          <button onClick={initiazaPlata} disabled={loading}
            style={{ flex: 2, padding: '12px', fontSize: '15px', fontWeight: '700', border: 'none', borderRadius: '10px', cursor: 'pointer',
                     background: '#2d2d7e', color: '#fff', opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Se procesează...' : `Plătește ${fmt(suma)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BtnPlata({ label, suma, onClick, disabled }) {
  if (disabled) return null;
  return (
    <button onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
               fontSize: '12px', fontWeight: '600', border: '1.5px solid #2d2d7e',
               borderRadius: '8px', cursor: 'pointer', background: '#eef2ff', color: '#2d2d7e',
               whiteSpace: 'nowrap' }}>
      💳 {label}
    </button>
  );
}

function StatusBadge({ platit, dataScadenta, datePlata, nrChitanta, metoda }) {
  if (platit) return (
    <span style={{ background: '#f0fdf4', color: '#166534', borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
      ✓ Achitat {datePlata ? fmtDate(datePlata) : ''}{nrChitanta ? ` · #${nrChitanta}` : ''}{metoda === 'online' ? ' · Online' : ''}
    </span>
  );
  if (isExpired(dataScadenta)) return (
    <span style={{ background: '#fef2f2', color: '#991b1b', borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
      ⚠ Expirat ({fmtDate(dataScadenta)})
    </span>
  );
  return (
    <span style={{ background: '#fffbeb', color: '#92400e', borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
      ⏳ Scadent {fmtDate(dataScadenta)}
    </span>
  );
}

function PropCard({ prop, onPay }) {
  const [open, setOpen] = useState(false);
  const cfg = TIP_CFG[prop.tip] || TIP_CFG.cladire;
  const areImpozit = !!prop.impozit_id;
  const platitIntegral = prop.platit_integral;

  function descriere() {
    if (prop.tip === 'cladire') {
      return [
        prop.tip_cladire === 'rezidentiala' ? 'Rezidențială' : prop.tip_cladire === 'nerezidentiala' ? 'Nerezidențială' : null,
        prop.suprafata_utila && `${prop.suprafata_utila} mp util`,
        prop.an_constructie && `an ${prop.an_constructie}`,
        prop.nr_camere && `${prop.nr_camere} camere`,
      ].filter(Boolean).join(' · ');
    }
    if (prop.tip === 'teren') {
      return [
        prop.suprafata_teren && `${prop.suprafata_teren} mp`,
        prop.categorie_teren?.replace(/_/g, ' '),
        prop.zona_teren && `Zona ${prop.zona_teren}`,
      ].filter(Boolean).join(' · ');
    }
    if (prop.tip === 'transport') {
      return [prop.marca, prop.model, prop.nr_inmatriculare, prop.an_fabricatie,
              prop.capacitate_cilindrica && `${prop.capacitate_cilindrica} cm³`]
        .filter(Boolean).join(' · ');
    }
    return '';
  }

  const unpaidTotal = areImpozit && !platitIntegral && (!prop.platit_rata1 || !prop.platit_rata2);

  return (
    <div style={{ background: '#fff', border: `1.5px solid ${unpaidTotal ? '#e5e7eb' : '#d1fae5'}`, borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer' }}
           onClick={() => setOpen(o => !o)}>
        <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: cfg.color + '18',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
          {cfg.icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>
            {prop.adresa || descriere() || 'Proprietate'}
          </div>
          {prop.adresa && descriere() && (
            <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{descriere()}</div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {areImpozit
            ? <>
                <div style={{ fontWeight: '700', fontSize: '15px', color: cfg.color }}>{fmt(prop.suma_totala)}</div>
                {platitIntegral && <div style={{ fontSize: '11px', color: '#22c55e', fontWeight: '600' }}>✓ Achitat integral</div>}
              </>
            : <div style={{ fontSize: '12px', color: '#9ca3af' }}>Nicio taxă înregistrată</div>
          }
        </div>
        <span style={{ color: '#9ca3af', fontSize: '12px' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && areImpozit && (
        <div style={{ borderTop: '1px solid #f3f4f6', padding: '14px 16px', background: '#fafafa' }}>
          {platitIntegral ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px',
                          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
              <span style={{ fontSize: '22px' }}>✅</span>
              <div>
                <div style={{ fontWeight: '600', color: '#166534', fontSize: '14px' }}>Impozit achitat integral</div>
                {prop.data_plata_integral && (
                  <div style={{ fontSize: '12px', color: '#4ade80' }}>
                    {fmtDate(prop.data_plata_integral)}
                    {prop.nr_chitanta_integral ? ` · Chitanță #${prop.nr_chitanta_integral}` : ''}
                    {prop.metoda_plata ? ` · ${METODA_LABEL[prop.metoda_plata] || prop.metoda_plata}` : ''}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Rata I */}
              <div style={{ padding: '12px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Rata I — {fmt(prop.rata1)}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>Scadentă {fmtDate(prop.data_scadenta_1)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <StatusBadge platit={prop.platit_rata1} dataScadenta={prop.data_scadenta_1}
                      datePlata={prop.data_plata_rata1} nrChitanta={prop.nr_chitanta_rata1} metoda={prop.metoda_plata} />
                    {!prop.platit_rata1 && (
                      <BtnPlata label={`Plătește Rata I`} suma={prop.rata1} onClick={() => onPay(prop, '1')} />
                    )}
                  </div>
                </div>
              </div>

              {/* Rata II */}
              <div style={{ padding: '12px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Rata II — {fmt(prop.rata2)}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>Scadentă {fmtDate(prop.data_scadenta_2)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <StatusBadge platit={prop.platit_rata2} dataScadenta={prop.data_scadenta_2}
                      datePlata={prop.data_plata_rata2} nrChitanta={prop.nr_chitanta_rata2} metoda={prop.metoda_plata} />
                    {!prop.platit_rata2 && (
                      <BtnPlata label="Plătește Rata II" suma={prop.rata2} onClick={() => onPay(prop, '2')} />
                    )}
                  </div>
                </div>
              </div>

              {/* Plată integrală */}
              {(!prop.platit_rata1 || !prop.platit_rata2) && (
                <div style={{ padding: '12px 14px', background: '#eef2ff', border: '1.5px solid #c7d2fe', borderRadius: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#2d2d7e' }}>
                        Plată integrală {isBefore31March() ? `— ${fmt(parseFloat(prop.suma_totala) * 0.9)} (−10%)` : `— ${fmt(prop.suma_totala)}`}
                      </div>
                      {isBefore31March() && (
                        <div style={{ fontSize: '11px', color: '#6366f1', marginTop: '2px' }}>
                          Bonificație 10% pentru plata înainte de 31 martie
                        </div>
                      )}
                    </div>
                    <BtnPlata label="Plătește integral" suma={prop.suma_totala} onClick={() => onPay(prop, 'integral')} />
                  </div>
                </div>
              )}
            </div>
          )}
          {prop.obs_impozit && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#888', fontStyle: 'italic' }}>{prop.obs_impozit}</div>
          )}
        </div>
      )}
    </div>
  );
}

function SumarCard({ sumar }) {
  if (!sumar) return null;
  const w        = useWindowWidth();
  const isMobile = w < 640;
  const dePlata  = parseFloat(sumar.total_de_plata || 0);
  const platit   = parseFloat(sumar.total_platit || 0);
  const ramas    = Math.max(0, dePlata - platit);
  const pct      = dePlata > 0 ? Math.round((platit / dePlata) * 100) : 0;
  const expirate = parseInt(sumar.rata1_expirate || 0) + parseInt(sumar.rata2_expirate || 0);

  return (
    <div style={{ background: '#2d2d7e', borderRadius: '14px', padding: isMobile ? '16px' : '20px 24px', color: '#fff', marginBottom: '24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: isMobile ? '12px' : '16px', marginBottom: '14px' }}>
        {[
          ['Total de plată', fmt(dePlata), '#fff',   false],
          ['Achitat',        fmt(platit),  '#4ade80', false],
          ['Rămas',          fmt(ramas),   ramas > 0 ? '#fbbf24' : '#4ade80', isMobile],
        ].map(([lbl, val, clr, fullRow]) => (
          <div key={lbl} style={fullRow ? { gridColumn: '1 / -1' } : {}}>
            <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{lbl}</div>
            <div style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '700', color: clr }}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '6px', height: '6px', marginBottom: '8px' }}>
        <div style={{ width: `${pct}%`, background: '#4ade80', borderRadius: '6px', height: '100%', transition: 'width 0.4s' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', opacity: 0.8 }}>
        <span>{pct}% achitat</span>
        {expirate > 0 && <span style={{ color: '#fca5a5' }}>⚠ {expirate} {expirate === 1 ? 'rată expirată' : 'rate expirate'}</span>}
      </div>
    </div>
  );
}

export default function ImpozitePage() {
  const anCurent = new Date().getFullYear();
  const [an, setAn] = useState(anCurent);
  const [modalPlata, setModalPlata] = useState(null); // { prop, rata }
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();

  const { data: result, isLoading } = useQuery(['impozite-mele', an], () => impoziteAPI.getMele(an));
  const { data: sumarResult }       = useQuery(['impozite-sumar', an], () => impoziteAPI.getSumar(an));

  // Reîncarcă dacă ne-am întors de la plată
  const orderId = searchParams.get('orderId');
  React.useEffect(() => {
    if (orderId) {
      setTimeout(() => {
        qc.invalidateQueries(['impozite-mele', an]);
        qc.invalidateQueries(['impozite-sumar', an]);
      }, 2000); // 2s delay pt IPN
    }
  }, [orderId]);

  const proprietati = result?.data || [];
  const sumar = sumarResult?.data;
  const grupe = { cladire: [], teren: [], transport: [] };
  for (const p of proprietati) { if (grupe[p.tip]) grupe[p.tip].push(p); }

  return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <PageHeader title="Impozite și taxe locale" subtitle="Situația impozitelor pentru bunurile înregistrate pe numele dvs." />
        <select value={an} onChange={e => setAn(parseInt(e.target.value))}
          style={{ padding: '8px 12px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px',
                   outline: 'none', background: '#fff', color: '#1a1a2e', fontFamily: 'inherit' }}>
          {Array.from({ length: 5 }, (_, i) => anCurent - i).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {orderId && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px',
                      padding: '12px 16px', marginBottom: '20px', fontSize: '14px', color: '#166534', textAlign: 'center' }}>
          ✓ Plata a fost procesată. Statusul impozitului va fi actualizat în câteva momente.
        </div>
      )}

      {isLoading ? <Spinner /> : (
        <>
          <SumarCard sumar={sumar} />

          {proprietati.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏛</div>
              <div style={{ fontWeight: '600', fontSize: '16px', color: '#374151', marginBottom: '6px' }}>Nu există proprietăți înregistrate</div>
              <div style={{ fontSize: '14px' }}>Contactați Biroul de Taxe și Impozite Locale pentru înregistrarea bunurilor dumneavoastră.</div>
            </div>
          ) : (
            Object.entries(TIP_CFG).map(([tip, cfg]) => {
              const lista = grupe[tip];
              if (!lista.length) return null;
              return (
                <div key={tip} style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '18px' }}>{cfg.icon}</span>
                    <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a1a2e' }}>{cfg.label}</span>
                    <span style={{ background: cfg.color + '18', color: cfg.color, borderRadius: '12px',
                                   padding: '2px 8px', fontSize: '12px', fontWeight: '600' }}>{lista.length}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {lista.map(p => <PropCard key={p.id} prop={p} onPay={(prop, rata) => setModalPlata({ prop, rata })} />)}
                  </div>
                </div>
              );
            })
          )}

          <div style={{ marginTop: '32px', padding: '14px 16px', background: '#f9fafb', borderRadius: '10px',
                        fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
            <strong>Informații:</strong> Impozitele locale se achită în două rate egale — Rata I până la 31 martie,
            Rata II până la 30 septembrie. Plata integrală până la 31 martie beneficiază de o bonificație de 10%.
            Pentru clarificări, contactați Biroul Taxe și Impozite Locale.
          </div>
        </>
      )}

      {modalPlata && (
        <ModalPlataOnline
          prop={modalPlata.prop}
          rata={modalPlata.rata}
          onClose={() => setModalPlata(null)}
        />
      )}
    </Layout>
  );
}
